import { useContext, useEffect, useMemo } from 'react';

import { FiX } from 'react-icons/fi';
import styled from '@emotion/styled';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  clearPreparationCentre,
  selectPreparationCentre,
  selectSelectedCountryName,
} from 'store/country/countrySlice';
import {
  hideChildMigrationIsAvailableNotificationBar,
  selectUser,
  hideShowInboxNotificationBar,
  hideShowPrsNotificationBar,
} from 'store/user/userSlice';
import { useAppDispatch, useAppSelector } from 'store';
import { AppSettingsContext, ROUTES } from 'core';
import { useLazyGetUnreadMessagesCountQuery } from 'store/inbox/inboxApi';

import { Container, SimpleHeader } from '@britishcouncil/react-solas';
import { OidcContext } from '@britishcouncil/react-oidc-auth';

import { useLazyGetSessionsAvailableInPrsQuery } from "../../store/post-results-services/postResultsServicesApi";

import { AuthNavbar, MainNavbar } from './components';

export const Header = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const country = useAppSelector(selectSelectedCountryName);
  const pcInfo = useAppSelector(selectPreparationCentre);
  const loggedUser = useAppSelector(selectUser);
  const userManager = OidcContext.useAuth();
  const navigate = useNavigate();
  const appSettings = useContext(AppSettingsContext);

  const handleLoginClick = () => {
    userManager.signinRedirect();
  };

  const handleLogoutClick = () => {
    dispatch(clearPreparationCentre());
    navigate(ROUTES.LOGOUT);
  };
  const [sessionInPrsQuery, { data: sessionsInPrs }] = useLazyGetSessionsAvailableInPrsQuery();
  const [unreadMessageQuery, { data: unreadMessagesCount }] = useLazyGetUnreadMessagesCountQuery();

  const userName = useMemo(
    () => {
      if (!loggedUser?.isFullyRegistered) return '';

      const { firstName, lastName } = loggedUser;

      return lastName?.trim()
        ? `${firstName} ${lastName}`
        : firstName;
    },
    [loggedUser]
  );

  const showMigrationNotification = useMemo(
    () =>
      (loggedUser?.hideNotificationAboutAvailableMigration ?? false) === false &&
      (loggedUser?.hasChildWhoRequireMigration ?? false),
    [loggedUser]
  );

  useEffect(() => {
    if (loggedUser) unreadMessageQuery();
  }, [loggedUser, unreadMessageQuery]);

  useEffect(() => {
    if (loggedUser && appSettings.prsFeatureEnabled) sessionInPrsQuery();
  }, [appSettings.prsFeatureEnabled, loggedUser, sessionInPrsQuery]);

  const showInboxNotification = useMemo(
    () =>
      (loggedUser?.hideShowInboxNotificationBar ?? false) === false && ((unreadMessagesCount ?? 0) > 0 ? true : false),
    [loggedUser, unreadMessagesCount]
  );

  const showPrsNotification = useMemo(
    () => !(loggedUser?.hideShowPrsNotificationBar ?? false) ? (sessionsInPrs?.names ?? []).length > 0 : false,
    [loggedUser, sessionsInPrs]
  );

  return (
    <NotificationBarContainer>
      {showMigrationNotification && (
        <NotificationBar className="alert alert-info">
          <Container>
            <Wrapper>
              The candidate is over 18 years old, to create registration - migration of account is required, please go
              to <StyledLink to={`${ROUTES.MY_PROFILE}?showMigrationAvailable=true`}>My profiles </StyledLink>
              tab.
              <StyledButton
                className="btn-link link"
                onClick={() => dispatch(hideChildMigrationIsAvailableNotificationBar())}
              >
                <FiX></FiX>
              </StyledButton>
            </Wrapper>
          </Container>
        </NotificationBar>
      )}

      {showInboxNotification && !(location.pathname === ROUTES.INBOX) && (
        <NotificationBar className="alert alert-info">
          <Container>
            <Wrapper>
              <InboxNotificationWrapper>
                Please notice: You have important documents related to exams, Check the
                <StyledLink to={`${ROUTES.INBOX}`}> Inbox.</StyledLink>
              </InboxNotificationWrapper>

              <StyledButton className="btn-link link" onClick={() => dispatch(hideShowInboxNotificationBar())}>
                <FiX></FiX>
              </StyledButton>
            </Wrapper>
          </Container>
        </NotificationBar>
      )}

      {showPrsNotification && sessionsInPrs && sessionsInPrs.names.length > 0 && (
        <NotificationBar className="alert alert-info">
          <Container>
            <Wrapper>
              <PrsNotificationWrapper>
                You can now apply for Post Results Services for the following exam series:
                <NavbarList>
                  <ul>
                    {sessionsInPrs.names.map(((session, idx) => {
                      return (
                        <li key={idx}>
                          {session}
                          {idx === sessionsInPrs.names.length - 1 && (<>.</>)}
                        </li>
                      );
                    }))}
                  </ul>
                  <br />
                  Apply for <StyledLink to={`${ROUTES.POST_RESULTS_SERVICES}`}>Post Result Service</StyledLink>
                </NavbarList>
              </PrsNotificationWrapper>
              <StyledButton className="btn-link link" onClick={() => dispatch(hideShowPrsNotificationBar())}>
                <FiX></FiX>
              </StyledButton>
            </Wrapper>
          </Container>
        </NotificationBar>
      )}

      <HeaderWrapper className={country ? 'navbar-with-country' : ''}>
        <SimpleHeader
          slim
          centered
          hasBorderBottom
          size="large"
          slogan={
            <>
              <span className="bc-brand-slogan">Schools</span>
              {country && <span className="bc-brand-slogan country-slogan">{country}</span>}
            </>
          }
        >
          <AuthNavbar
            isLoggedIn={!!loggedUser}
            username={userName}
            onLoginClick={handleLoginClick}
            onLogoutClick={handleLogoutClick}
          />
          <MainNavbar isLoggedIn={!!loggedUser && loggedUser.isFullyRegistered} />
          {pcInfo && <PreparationCentre>{pcInfo.preparationCentreName}</PreparationCentre>}
        </SimpleHeader>
      </HeaderWrapper>
    </NotificationBarContainer>
  );
};

const StyledLink = styled(NavLink)`
    padding-left: 5px;
    padding-right: 5px;
    color: blue !important;
    text-decoration: underline;
`;

const InboxNotificationWrapper = styled.div`
    margin-left: auto;
`;

const PrsNotificationWrapper = styled.div`
    margin-left: auto;
`;

const Wrapper = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 10px 0 10px 0;
`;

const StyledButton = styled.button`
    display: flex;
    margin-left: auto;
`;

const PreparationCentre = styled.h3`
    float: right;
    margin: 20px 0;

    @media (min-width: 850px) {
        position: absolute;
        bottom: 0;
        right: 10px;
        margin-bottom: 48px;
    }
`;

const NotificationBar = styled.div`
    padding: 0;
    margin-bottom: 0px;
    text-align: center;
`;

const NotificationBarContainer = styled.div`
    .alert + .alert {
        margin-top: 0;
    }
`;

const NavbarList = styled.div`
    text-align: center;

    ul {
        display: inline-block;
        padding: 0;
        margin: 0;
        text-align: left;
    }
`;

const HeaderWrapper = styled.div`
    &.navbar-with-country {
        .navbar-default > .container {
            div[role='navigation'] > ul.nav.navbar-nav.bc-navbar-support {
                padding-top: 3px;
                margin-top: 2px;
            }

            .navbar-brand {
                height: 55px;

                .country-slogan {
                    margin-top: 10px;
                }

                @media (min-width: 850px) {
                    height: 34px;

                    .country-slogan {
                        margin-top: 0;
                    }
                }
            }
        }
    }
`;
